import { useCallback, useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useAppStore } from '@core/store'
import {
  RIGHT_SIDEBAR_MAX,
  RIGHT_SIDEBAR_MIN,
} from '@viewer/store/selectionSlice'
import PropertiesPanel from '@viewer/inspector/PropertiesPanel'

function RightSidebar() {
  const width = useAppStore((s) => s.rightSidebarWidth)
  const setWidth = useAppStore((s) => s.setRightSidebarWidth)
  const setMode = useAppStore((s) => s.setRightSidebarMode)
  const collapsed = useAppStore((s) => s.rightSidebarCollapsed)
  const setCollapsed = useAppStore((s) => s.setRightSidebarCollapsed)
  const dragging = useRef(false)

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      dragging.current = true
      ;(e.target as Element).setPointerCapture?.(e.pointerId)
    },
    [],
  )

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!dragging.current) return
      setWidth(window.innerWidth - e.clientX)
    }
    const onUp = () => {
      dragging.current = false
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [setWidth])

  // Collapsed: thin strip with an expand chevron on its left edge,
  // mirroring the left sidebar's pattern. The user always has a way
  // to bring the panel back without diving into a menu.
  if (collapsed) {
    return (
      <aside className="flex w-7 shrink-0 flex-col items-center border-l border-[var(--color-border)] bg-[var(--color-panel)]">
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          title="Expandir Propriedades"
          aria-label="Expandir Propriedades"
          className="mt-2 flex h-7 w-7 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-panel-elevated)] hover:text-[var(--color-text)]"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="mt-3 [writing-mode:vertical-rl] text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
          Propriedades
        </div>
      </aside>
    )
  }

  return (
    <aside
      className="relative flex shrink-0 flex-col border-l border-[var(--color-border)] bg-[var(--color-surface)]"
      style={{
        width: `${width}px`,
        minWidth: `${RIGHT_SIDEBAR_MIN}px`,
        maxWidth: `${RIGHT_SIDEBAR_MAX}px`,
      }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        onPointerDown={onPointerDown}
        className="absolute -left-[3px] top-0 z-10 h-full w-[6px] cursor-col-resize hover:bg-[var(--color-accent)]/40"
      />
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-2">
          <span className="text-sm font-medium text-[var(--color-text)]">
            Propriedades
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setMode('floating')}
              title="Destacar como janela flutuante dentro da página (drag/resize, mas presa ao browser)."
              className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            >
              ↗ flutuante
            </button>
            <button
              type="button"
              onClick={() => setMode('window')}
              title="Abrir como janela real do browser (arrastável para outro monitor). Pode ser bloqueada por popup blocker."
              className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            >
              ↗ janela browser
            </button>
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              title="Colapsar para o lado direito"
              aria-label="Colapsar"
              className="flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
        <PropertiesPanel />
      </div>
    </aside>
  )
}

export default RightSidebar
